import React, { useEffect, useState } from 'react'
import {
  CButton,
  CFormFeedback,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CMultiSelect,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCalendar, cilGamepad } from '@coreui/icons'

import ClubService from 'src/service/ClubService'
import UserService from 'src/service/UserService'
import ToastComponent from 'src/components/common/TaostComponent'

// Values match `membershipType` on users/updateMembershipStatus.
const GAME = '1'
const ANNUAL = '2'

const PLAN_OPTIONS = [
  {
    value: GAME,
    label: 'Game Membership',
    hint: 'Paid for specific games only. Replaces any game memberships they already have.',
    icon: cilGamepad,
  },
  {
    value: ANNUAL,
    label: 'Annual Membership',
    hint: 'Paid for every game for the year.',
    icon: cilCalendar,
  },
]

// Marks a member as paid, either for chosen games or for an annual membership.
const AssignMembershipPlan = ({ member, visible, onClose, onSaved }) => {
  const [plan, setPlan] = useState(GAME)
  const [games, setGames] = useState([])
  const [gamesLoading, setGamesLoading] = useState(false)
  const [selectedGames, setSelectedGames] = useState([])
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!visible || !member?.id) return
    setPlan(GAME)
    setSelectedGames([])
    setError(null)
    setGames([])
    setGamesLoading(true)
    ClubService.getUserAllActivatedGameList(member.id)
      .then((res) => {
        if (res.status === 200) setGames(res.data || [])
      })
      .catch(() => ToastComponent('Could not load this member’s games', 'error'))
      .finally(() => setGamesLoading(false))
  }, [visible, member?.id])

  const choosePlan = (value) => {
    setPlan(value)
    setSelectedGames([])
    setError(null)
  }

  const handleSave = () => {
    if (plan === GAME && selectedGames.length === 0) {
      setError('Pick at least one game.')
      return
    }
    setSaving(true)
    UserService.changeMembership({
      userId: member.id,
      status: 1,
      membershipType: plan,
      gameIds: plan === GAME ? selectedGames : [],
    })
      .then((res) => {
        ToastComponent(res.message, 'success')
        onSaved()
        onClose()
      })
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not update membership', 'error')
      })
      .finally(() => setSaving(false))
  }

  const saveLabel =
    plan === GAME && selectedGames.length > 0
      ? `Mark Paid for ${selectedGames.length} Game${selectedGames.length > 1 ? 's' : ''}`
      : 'Mark as Paid'

  return (
    <CModal visible={visible} onClose={onClose} alignment="center">
      <CModalHeader>
        <CModalTitle>Mark membership as paid</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p className="um-muted mb-3">
          Choose what <strong className="text-body">{member?.full_name}</strong> has paid for.
        </p>

        <div className="mm-plan-options" role="radiogroup" aria-label="Membership type">
          {PLAN_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={plan === opt.value}
              className={`mm-plan-option${plan === opt.value ? ' active' : ''}`}
              onClick={() => choosePlan(opt.value)}
            >
              <span className="mm-plan-icon">
                <CIcon icon={opt.icon} />
              </span>
              <span className="text-start">
                <span className="d-block fw-semibold">{opt.label}</span>
                <span className="d-block um-muted small">{opt.hint}</span>
              </span>
            </button>
          ))}
        </div>

        {plan === GAME && (
          <div className="mt-3">
            <div className="fw-semibold small mb-1">Games</div>
            {gamesLoading ? (
              <div className="d-flex align-items-center gap-2 um-muted small py-2">
                <CSpinner size="sm" /> Loading games...
              </div>
            ) : games.length === 0 ? (
              <div className="mm-empty">
                This member has no active games, so only an annual membership can be given.
              </div>
            ) : (
              <>
                <CMultiSelect
                  options={games}
                  selectionType="tags"
                  placeholder="Select games"
                  invalid={!!error}
                  onChange={(selected) => {
                    setSelectedGames(selected.map((item) => item.value))
                    setError(null)
                  }}
                />
                {error && <CFormFeedback className="d-block">{error}</CFormFeedback>}
              </>
            )}
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </CButton>
        <CLoadingButton
          color="primary"
          loading={saving}
          disabled={plan === GAME && !gamesLoading && games.length === 0}
          onClick={handleSave}
        >
          {saveLabel}
        </CLoadingButton>
      </CModalFooter>
    </CModal>
  )
}

export default AssignMembershipPlan
